import { useSite } from "@inrange/building-manager-api-client";
import {
  PartialSite,
  RecursivePartial,
  Site,
  SiteCalculations,
} from "@inrange/building-manager-api-client/models-site";
import { createContextTS } from "@inrange/utils";
import hash from "object-hash";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { buildSiteCalculationPayload } from "./buildSiteCalculationPayload";

const SiteCalculationsContext = createContextTS<{
  clientsideValidations: Record<string, string | undefined>;
  setClientsideValidations: React.Dispatch<
    React.SetStateAction<Record<string, string | undefined>>
  >;
  errors: Record<string, string>;
  siteCalculations: SiteCalculations;
  isCalculating: boolean;
}>();

const SiteCalculationsProvider: React.FC<{
  userOrgId?: string;
  app: "admin" | "customer";
  site: RecursivePartial<Site>;
  originalSite: PartialSite;
  setSaveDisabled?: (disabled: boolean) => void;
  isBulkEdit?: boolean;
  children: ReactNode;
  disableUpdates?: boolean;
}> = ({
  userOrgId,
  app,
  site,
  originalSite,
  setSaveDisabled,
  isBulkEdit,
  children,
  disableUpdates,
}) => {
  const { previewSite } = useSite({ userOrgId, app });

  const [invalidStates, setInvalidStates] = useState<any[]>([]);
  const [clientsideValidations, setClientsideValidations] = useState<
    Record<string, string | undefined>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewError, setPreviewError] = useState(false);
  const [hasSdmInputsDataHashError, setHasSdmInputsDataHashError] =
    useState(false);
  const [showSdmInputsDataHashError, setShowSdmInputsDataHashError] =
    useState(true);

  // Track request IDs to prevent stale responses from overwriting newer ones
  const latestRequestIdRef = useRef(0);
  const appliedRequestIdRef = useRef(0);

  const [siteCalculations, setSiteCalculations] = useState<SiteCalculations>({
    // >> Location linked data
    latitude: originalSite.latitude ?? 51.509865,
    longitude: originalSite.longitude ?? -0.118092,
    countryCode: originalSite.countryCode || "GBR",
    timeZone: originalSite.timeZone || "Europe/London",
    currencyCode: originalSite.currencyCode || "GBP",
    areaUnit: originalSite.areaUnit || "ft",
    dno: originalSite.dno || undefined,
    substationData: originalSite.substationData || undefined,
    // >> Buildings
    totalBuildingArea: originalSite.totalBuildingArea || 0,
    // >> Esys / Generation / Demand / Battery
    esys: originalSite.esys || {},
    demand: originalSite.demand || {},
    generation: originalSite.generation || {},
    battery: originalSite.battery || {},
    tenantAnnualDemandKwh: originalSite.tenantAnnualDemandKwh || 0,
    // >> Site summary stats
    totalInstalledCapacity: originalSite.totalInstalledCapacity || 0,
    totalRoofUtil: originalSite.totalRoofUtil || 0,
    totalInverters: originalSite.totalInverters || [],
    totalExportLimit: originalSite.totalExportLimit,
    totalBatterySummary: originalSite.totalBatterySummary || undefined,
    pvPanelCount: originalSite.pvPanelCount || 0,
    generationLossFactors: originalSite.generationLossFactors || undefined,
    costInputsPv: originalSite.costInputsPv || undefined,
    hasAllZeroExportLimit: originalSite.hasAllZeroExportLimit || false,
    // >> Battery
    costInputsBattery: originalSite.costInputsBattery || undefined,
    // >> Energy flow inputs
    aggHalfHourlyGeneration: originalSite.aggHalfHourlyGeneration || undefined,
    // >> Energy flow outputs
    energyFlowAnnual: originalSite.energyFlowAnnual || undefined,
    energyFlowMonthly: originalSite.energyFlowMonthly || undefined,
    // >> Tariffs
    blendedExportTariff: originalSite.blendedExportTariff || 0,
    // >> Deal and contract attributes
    leaseLengthMonths: originalSite.leaseLengthMonths || 0,
    // >> Financial modeling - inputs
    activeInvestmentModel: originalSite.activeInvestmentModel || "lease",
    // >> Financial modeling - outputs
    projectCosts: originalSite.projectCosts || undefined,
    financialModels: originalSite.financialModels || undefined,
    financialModelsInrange: originalSite.financialModelsInrange || undefined,
  });

  useEffect(() => {
    const updatedErrors: Record<string, string> = {};
    invalidStates.forEach((error) => {
      // the error response inside invalidStates can look like this:
      //   {
      //       "loc": [
      //           "costInputs",
      //           "panelRate"
      //       ],
      //       "msg": "Must be a number.",
      //       "type": "type_error.float"
      //   }
      // we can therefore join the loc array to get the path to the error
      // i.e. in this case, it would be "costInputs.panelRate"
      updatedErrors[
        error.loc.filter((elem: string) => "__root__" !== elem).join(".")
      ] = error.msg;
    });
    setHasSdmInputsDataHashError(
      Object.keys(updatedErrors).find((key) =>
        key.endsWith(".sdmInputsDataHash")
      ) !== undefined
    );
    setShowSdmInputsDataHashError(true);
    setErrors(updatedErrors);
  }, [invalidStates]);

  const previewSiteMutate = previewSite.mutate;
  const updateCalculations = useCallback(
    (site: RecursivePartial<Site>) => {
      if (setSaveDisabled) {
        setSaveDisabled(true);
      }
      setShowSdmInputsDataHashError(false);

      // Increment the request ID to track this specific request
      const requestId = ++latestRequestIdRef.current;

      previewSiteMutate(
        { site },
        {
          onSuccess: (data) => {
            // Only apply results if this is from the latest request
            // (ignore stale responses from earlier requests that completed late)
            if (requestId < latestRequestIdRef.current) {
              return;
            }
            // Only apply if we haven't already applied a newer result
            if (requestId <= appliedRequestIdRef.current) {
              return;
            }
            appliedRequestIdRef.current = requestId;

            setInvalidStates([]);
            setPreviewError(false);
            setSiteCalculations((prevCalculations) => ({
              ...prevCalculations,
              ...data.site,
              // Give local code access to a millisecond accuracy time of when this calculation was completed
              updatedAt: Date.now() / 1000.0,
            }));
          },
          onError: (error) => {
            // Only apply error state if this is from the latest request
            if (requestId < latestRequestIdRef.current) {
              return;
            }
            if (requestId <= appliedRequestIdRef.current) {
              return;
            }
            appliedRequestIdRef.current = requestId;

            const newErrors = error.response?.data?.invalid_states ?? [];
            setInvalidStates(error.response?.data?.invalid_states ?? []);
            if (newErrors.length > 0) {
              setPreviewError(false);
            } else {
              // We got an error, but we don't know in which field
              setPreviewError(true);
            }
          },
          onSettled: () => {
            if (setSaveDisabled) {
              setSaveDisabled(false);
            }
          },
        }
      );
    },
    [previewSiteMutate, setSaveDisabled]
  );

  const [previousSiteHash, setPreviousSiteHash] = useState<string>(() =>
    disableUpdates
      ? ""
      : hash(
          buildSiteCalculationPayload({
            site: originalSite,
            clearNonCalcFields: true,
          })
        )
  );
  // const [previousSitePayload, setPreviousSitePayload] = useState<any>(() =>
  //   disableUpdates ? null : buildSitePayload(originalSite)
  // );
  // const previousSitePayloadRef = React.useRef(previousSitePayload);
  // previousSitePayloadRef.current = previousSitePayload;

  useEffect(() => {
    if (disableUpdates) return;
    if (Object.keys(site).length === 0) return;
    const updateSitePayload = buildSiteCalculationPayload({
      site,
      clearNonCalcFields: true,
    });
    const updatedSiteHash = hash(updateSitePayload);
    if (previousSiteHash === updatedSiteHash) return;
    if (previousSiteHash === "") {
      setPreviousSiteHash(updatedSiteHash);
      return;
    }

    // // >>> DEBUG: Log which fields changed causing hash mismatch
    // const logFieldChanges = (prev: any, curr: any, path = ""): void => {
    //   if (prev === curr) return;
    //   if (
    //     typeof prev !== "object" ||
    //     typeof curr !== "object" ||
    //     prev === null ||
    //     curr === null
    //   ) {
    //     if (prev !== curr)
    //       console.log(`[SiteHash] Changed: ${path}`, { prev, curr });
    //     return;
    //   }
    //   const allKeys = new Set([
    //     ...Object.keys(prev || {}),
    //     ...Object.keys(curr || {}),
    //   ]);
    //   allKeys.forEach((key) => {
    //     const newPath = path ? `${path}.${key}` : key;
    //     if (!(key in prev))
    //       console.log(`[SiteHash] Added: ${newPath}`, { value: curr[key] });
    //     else if (!(key in curr))
    //       console.log(`[SiteHash] Removed: ${newPath}`, { value: prev[key] });
    //     else logFieldChanges(prev[key], curr[key], newPath);
    //   });
    // };
    // if (previousSitePayloadRef.current) {
    //   console.log("[SiteHash] Hash mismatch detected, analyzing changes...");
    //   logFieldChanges(previousSitePayloadRef.current, updateSitePayload);
    // }
    // setPreviousSitePayload(updateSitePayload);
    // // <<< END DEBUG

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;

    // Debounce so we don't recalculate immediately on keypress
    const debounceWaitMillis = testMode ? 0 : 300;
    const timeoutId = setTimeout(() => {
      setPreviousSiteHash(updatedSiteHash);
      updateCalculations(updateSitePayload);
    }, debounceWaitMillis);

    return () => clearTimeout(timeoutId);
  }, [site, previousSiteHash, updateCalculations, disableUpdates]);

  // NOTE: We don't show <LoadingIndicator> and <ErrorMessage> in the customer app
  return (
    <SiteCalculationsContext.Provider
      value={{
        clientsideValidations,
        setClientsideValidations,
        errors,
        siteCalculations,
        isCalculating: previewSite.isLoading,
      }}
    >
      {app === "admin" && previewSite.isLoading && !isBulkEdit && (
        <LoadingIndicator
          label="Calculating ..."
          testid="calculating-indicator"
        />
      )}
      {app === "admin" &&
        !previewSite.isLoading &&
        previewError &&
        !isBulkEdit && (
          <ErrorMessage
            label="Site calculations failed to load"
            testid="calculating-indicator"
          />
        )}
      {app === "admin" &&
        !previewSite.isLoading &&
        hasSdmInputsDataHashError &&
        showSdmInputsDataHashError && (
          <ErrorMessage
            label="Can't change energy flows with applied SDM matches"
            testid="calculating-indicator"
            onClose={() => setShowSdmInputsDataHashError(false)}
          />
        )}
      {children}
    </SiteCalculationsContext.Provider>
  );
};

export { SiteCalculationsContext, SiteCalculationsProvider };

interface LoadingIndicatorProps {
  label: string;
  testid: string;
}

const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  label,
  testid,
}) => {
  return (
    <div
      className="alert text-center alert-primary"
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: "100%",
        // Must sit above the admin header Navbar (z-index 1030 in
        // admin-app Template.tsx). This fixed bar overlaps the header at
        // top: 0, so anything at or below the header's z-index is hidden
        // behind it. 1040 keeps it below Bootstrap modals (1050+).
        zIndex: 1040,
      }}
      role="alert"
      data-testid={testid}
    >
      {label}
    </div>
  );
};

interface ErrorProps {
  label: string;
  testid: string;
  onClose?: () => void;
}

const ErrorMessage: React.FC<ErrorProps> = ({ label, testid, onClose }) => {
  return (
    <div
      className="alert text-center alert-danger"
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: "100%",
        // Must sit above the admin header Navbar (z-index 1030 in
        // admin-app Template.tsx). This fixed bar overlaps the header at
        // top: 0, so anything at or below the header's z-index is hidden
        // behind it. 1040 keeps it below Bootstrap modals (1050+).
        zIndex: 1040,
      }}
      role="alert"
      data-testid={testid}
    >
      {label}
      {onClose && (
        <>
          {" "}
          -{" "}
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
              font: "inherit",
            }}
            data-testid="close-preview-error"
          >
            Close
          </button>
        </>
      )}
    </div>
  );
};
